import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { dataModelsService, type DataModelDetail } from '../../api/dataModelsService';
import { dataModelDisplayNames } from '../../utils/dataModelDisplayNames';

const EMPTY_LABELS: ReadonlyMap<string, string> = new Map();
const EditorDisplayNamesContext = createContext(EMPTY_LABELS);

/** One metadata request for all editor pickers; never changes chart or column data. */
export function EditorDisplayNamesProvider({ dataModelId, children }: {
  dataModelId?: string;
  children: ReactNode;
}) {
  const [model, setModel] = useState<DataModelDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    setModel(null);
    if (dataModelId) {
      dataModelsService.getById(dataModelId)
        .then((detail) => { if (!cancelled) setModel(detail); })
        .catch((error: unknown) => {
          if (!cancelled) console.error('Failed to load chart editor display names:', error);
        });
    }
    return () => { cancelled = true; };
  }, [dataModelId]);

  // Suppress previous-model labels immediately, including before effect cleanup.
  const labels = useMemo(() => model && model.id === dataModelId
    ? dataModelDisplayNames(model).labels
    : EMPTY_LABELS, [model, dataModelId]);

  return <EditorDisplayNamesContext.Provider value={labels}>{children}</EditorDisplayNamesContext.Provider>;
}

export function useEditorDisplayNames(): ReadonlyMap<string, string> {
  return useContext(EditorDisplayNamesContext);
}